import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { Extension, type JSONContent } from "@tiptap/core";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Color from "@tiptap/extension-color";
import FontFamily from "@tiptap/extension-font-family";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import TextAlign from "@tiptap/extension-text-align";
import TextStyle from "@tiptap/extension-text-style";
import StarterKit from "@tiptap/starter-kit";
import { type Editor, BubbleMenu, EditorContent, useEditor } from "@tiptap/react";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  ChevronDown,
  Highlighter,
  ImagePlus,
  Italic,
  Link2,
  List,
  ListOrdered,
  LoaderCircle,
  Quote,
  Sparkles,
  Type,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  getStoryEditorClassName,
  getStoryViewportClassName,
} from "@/editors/rich-text/story-pagination";
import {
  captureSelectionEnhanceRange,
  hasNonEmptySelection,
  SELECTION_ENHANCE_MAX_WORDS,
  type CapturedSelectionEnhanceRange,
} from "@/editors/rich-text/selectionEnhance";
import {
  replaceDocumentTextPreservingStructure,
} from "@/editors/rich-text/documentTextReplacement";
import { cn } from "@/lib/utils";

type LoreEditorProps = {
  autofocus?: boolean;
  contentClassName?: string;
  content: JSONContent;
  enhanceSelection?: {
    getCreditCost: (wordCount: number) => number;
    getDisabledReason: (wordCount: number) => string | null;
    loading?: boolean;
    onRequest: (selection: CapturedSelectionEnhanceRange) => void;
    onSelectionChange?: () => void;
  };
  frameClassName?: string;
  onEditorApiChange?: (api: RichTextEditorApi | null) => void;
  onChange: (content: JSONContent) => void;
  paginated?: boolean;
  placeholder: string;
};

export type RichTextEditorApi = {
  replaceDocumentText: (text: string) => void;
  replaceRangeText: (from: number, to: number, text: string) => void;
  selectBestMatchingText: (text: string) => boolean;
};

type TextStyleAttributes = {
  color?: string;
  fontFamily?: string;
  fontSize?: string;
};

type HighlightAttributes = {
  color?: string;
};

const FONT_OPTIONS = [
  { label: "Doc", value: "default" },
  { label: "Fraunces", value: "Fraunces" },
  { label: "Source", value: "\"Source Serif 4\"" },
  { label: "Libre", value: "\"Libre Baskerville\"" },
  { label: "Manrope", value: "Manrope" },
  { label: "Plex", value: "\"IBM Plex Sans\"" },
] as const;

const FONT_SIZE_OPTIONS = [
  { label: "12", value: "0.75rem" },
  { label: "14", value: "0.875rem" },
  { label: "16", value: "1rem" },
  { label: "18", value: "1.125rem" },
  { label: "24", value: "1.5rem" },
  { label: "32", value: "2rem" },
] as const;

const HIGHLIGHT_SWATCHES = ["#f6e3a1", "#f9c8c4", "#c9e6c7", "#c8dcf8"] as const;
const LINE_HEIGHT_OPTIONS = [
  { label: "0.5", value: "0.5" },
  { label: "1", value: "1" },
  { label: "1.5", value: "1.5" },
  { label: "2", value: "2" },
] as const;
const PAGE_SURFACE_WIDTH_PX = 768;
const PAGE_SURFACE_HEIGHT_PX = 1120;
const PAGE_CONTENT_PADDING_X_PX = 36;
const PAGE_CONTENT_PADDING_Y_PX = 36;
const PAGE_CONTENT_WIDTH_PX = PAGE_SURFACE_WIDTH_PX - (PAGE_CONTENT_PADDING_X_PX * 2);
const PAGE_CONTENT_HEIGHT_PX = PAGE_SURFACE_HEIGHT_PX - (PAGE_CONTENT_PADDING_Y_PX * 2);
const PAGE_GAP_PX = 32;

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    fontSize: {
      setFontSize: (fontSize: string) => ReturnType;
      unsetFontSize: () => ReturnType;
    };
    lineHeight: {
      setLineHeight: (lineHeight: string) => ReturnType;
      unsetLineHeight: () => ReturnType;
    };
  }
}

const FontSize = Extension.create({
  name: "fontSize",

  addOptions() {
    return {
      types: ["textStyle"],
    };
  },

  addGlobalAttributes() {
    return [
      {
        attributes: {
          fontSize: {
            default: null,
            parseHTML: (element: HTMLElement) => element.style.fontSize || null,
            renderHTML: (attributes: { fontSize?: string | null }) => {
              if (!attributes.fontSize) {
                return {};
              }

              return {
                style: `font-size: ${attributes.fontSize}`,
              };
            },
          },
        },
        types: this.options.types,
      },
    ];
  },

  addCommands() {
    return {
      setFontSize:
        (fontSize: string) =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize }).run(),
      unsetFontSize:
        () =>
        ({ chain }) =>
          chain().setMark("textStyle", { fontSize: null }).removeEmptyTextStyle().run(),
    };
  },
});

const LineHeight = Extension.create({
  name: "lineHeight",

  addGlobalAttributes() {
    return [
      {
        attributes: {
          lineHeight: {
            default: null,
            parseHTML: (element: HTMLElement) => element.style.lineHeight || null,
            renderHTML: (attributes: { lineHeight?: string | null }) => {
              if (!attributes.lineHeight) {
                return {};
              }

              return {
                style: `line-height: ${attributes.lineHeight}`,
              };
            },
          },
        },
        types: ["heading", "paragraph"],
      },
    ];
  },

  addCommands() {
    return {
      setLineHeight:
        (lineHeight: string) =>
        ({ commands }) =>
          commands.updateAttributes("paragraph", { lineHeight })
          || commands.updateAttributes("heading", { lineHeight }),
      unsetLineHeight:
        () =>
        ({ commands }) =>
          commands.resetAttributes("paragraph", "lineHeight")
          || commands.resetAttributes("heading", "lineHeight"),
    };
  },
});

function ToolbarButton({
  active,
  disabled,
  icon,
  label,
  onClick,
}: {
  active?: boolean;
  disabled?: boolean;
  icon: ReactNode;
  label: string;
  onClick: () => void;
}) {
  return (
    <Button
      aria-label={label}
      className={cn(
        "h-10 min-w-10 rounded-md border border-transparent px-3 text-[0.95rem] shadow-none",
        active
          ? "border-[#e5d7c3] bg-[#f5ede0] text-foreground"
          : "bg-transparent text-muted-foreground hover:bg-[#f8f3eb] hover:text-foreground",
      )}
      disabled={disabled}
      onClick={onClick}
      title={label}
      type="button"
      variant="ghost"
    >
      {icon}
    </Button>
  );
}

function buildExtensions(placeholder: string) {
  return [
    StarterKit.configure({
      heading: {
        levels: [1, 2, 3],
      },
    }),
    TextAlign.configure({
      alignments: ["left", "center", "right", "justify"],
      types: ["heading", "paragraph"],
    }),
    Placeholder.configure({
      placeholder,
    }),
    TextStyle,
    Color,
    FontFamily,
    FontSize,
    LineHeight,
    Highlight.configure({
      multicolor: true,
    }),
    Image.configure({
      allowBase64: true,
      inline: false,
    }),
  ];
}

function EditorToolbar({ editor }: { editor: Editor | null }) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  if (!editor) {
    return null;
  }

  const activeEditor = editor;
  const textStyle = activeEditor.getAttributes("textStyle") as TextStyleAttributes;
  const highlight = activeEditor.getAttributes("highlight") as HighlightAttributes;
  const activeFormat = activeEditor.isActive("heading", { level: 1 })
    ? "h1"
    : activeEditor.isActive("heading", { level: 2 })
      ? "h2"
      : activeEditor.isActive("heading", { level: 3 })
        ? "h3"
        : "paragraph";
  const activeAlignment = ["left", "center", "right", "justify"].find((alignment) =>
    activeEditor.isActive({ textAlign: alignment }),
  ) ?? "left";
  const activeFontFamily = textStyle.fontFamily || "default";
  const activeFontSize = textStyle.fontSize || "1rem";
  const activeLineHeight = (activeEditor.getAttributes("paragraph").lineHeight as string | undefined)
    || (activeEditor.getAttributes("heading").lineHeight as string | undefined)
    || "1.5";
  const label = (key: string, fallback: string) => t(key, { defaultValue: fallback });

  async function handleImageUpload(file: File | null) {
    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.addEventListener("load", () => {
      if (typeof reader.result !== "string") {
        return;
      }

      activeEditor
        .chain()
        .focus()
        .setImage({
          alt: file.name,
          src: reader.result,
          title: file.name,
        })
        .run();
    });

    reader.readAsDataURL(file);
  }

  function handleImageUrlInsert() {
    const imageUrl = window.prompt(label("lore.editor.toolbar.imageUrlPrompt", "Paste an image URL"));

    if (!imageUrl) {
      return;
    }

    activeEditor
      .chain()
      .focus()
      .setImage({
        alt: label("lore.editor.toolbar.imageAlt", "Inserted image"),
        src: imageUrl,
      })
      .run();
  }

  return (
    <div className="lore-editor-toolbar">
      <div className="lore-editor-toolbar__group">
        <Select
          onValueChange={(value) => {
            const chain = activeEditor.chain().focus();

            if (value === "paragraph") {
              chain.setParagraph().run();
              return;
            }

            chain.setHeading({ level: Number(value.slice(1)) as 1 | 2 | 3 }).run();
          }}
          value={activeFormat}
        >
          <SelectTrigger
            aria-label={label("lore.editor.toolbar.format", "Format")}
            className="h-9 w-[4.75rem] !rounded-md border-border/60 bg-white px-2.5 shadow-none"
          >
            <SelectValue placeholder={label("lore.editor.toolbar.format", "Format")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="paragraph">Text</SelectItem>
            <SelectItem value="h1">H1</SelectItem>
            <SelectItem value="h2">H2</SelectItem>
            <SelectItem value="h3">H3</SelectItem>
          </SelectContent>
        </Select>

        <Select
          onValueChange={(value) => {
            if (value === "default") {
              activeEditor.chain().focus().unsetFontFamily().run();
              return;
            }

            activeEditor.chain().focus().setFontFamily(value).run();
          }}
          value={activeFontFamily}
        >
          <SelectTrigger
            aria-label={label("lore.editor.toolbar.font", "Font")}
            className="h-9 w-[6.75rem] !rounded-md border-border/60 bg-white px-2.5 shadow-none"
          >
            <SelectValue placeholder={label("lore.editor.toolbar.font", "Font")} />
          </SelectTrigger>
          <SelectContent>
            {FONT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          onValueChange={(value) => activeEditor.chain().focus().setFontSize(value).run()}
          value={activeFontSize}
        >
          <SelectTrigger
            aria-label={label("lore.editor.toolbar.fontSize", "Size")}
            className="h-9 w-[4.5rem] !rounded-md border-border/60 bg-white px-2.5 shadow-none"
          >
            <SelectValue placeholder={label("lore.editor.toolbar.fontSize", "Size")} />
          </SelectTrigger>
          <SelectContent>
            {FONT_SIZE_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select
          onValueChange={(value) => activeEditor.chain().focus().setLineHeight(value).run()}
          value={activeLineHeight}
        >
          <SelectTrigger
            aria-label={label("lore.editor.toolbar.lineHeight", "Spacing")}
            className="h-9 w-[4.75rem] !rounded-md border-border/60 bg-white px-2.5 shadow-none"
          >
            <SelectValue placeholder={label("lore.editor.toolbar.lineHeight", "Spacing")} />
          </SelectTrigger>
          <SelectContent>
            {LINE_HEIGHT_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="lore-editor-toolbar__group">
        <ToolbarButton
          active={activeEditor.isActive("bold")}
          icon={<Bold className="h-4 w-4" />}
          label={label("lore.editor.toolbar.bold", "Bold")}
          onClick={() => activeEditor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          active={activeEditor.isActive("italic")}
          icon={<Italic className="h-4 w-4" />}
          label={label("lore.editor.toolbar.italic", "Italic")}
          onClick={() => activeEditor.chain().focus().toggleItalic().run()}
        />

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              aria-label={label("lore.editor.toolbar.listMenu", "List options")}
              className={cn(
                "inline-flex h-9 items-center gap-1 rounded-md border border-transparent px-2.5 text-sm transition",
                activeEditor.isActive("bulletList") || activeEditor.isActive("orderedList")
                  ? "border-[#e5d7c3] bg-[#f5ede0] text-foreground"
                  : "text-muted-foreground hover:bg-[#f8f3eb] hover:text-foreground",
              )}
              type="button"
            >
              {activeEditor.isActive("orderedList")
                ? <ListOrdered className="h-4 w-4" />
                : <List className="h-4 w-4" />}
              <ChevronDown className="h-3.5 w-3.5" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              className="z-50 min-w-44 rounded-md border border-[#e5d7c3] bg-white p-1.5 shadow-lg"
              sideOffset={8}
            >
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none transition hover:bg-[#f8f3eb] focus:bg-[#f8f3eb]"
                onSelect={() => activeEditor.chain().focus().toggleBulletList().run()}
              >
                <List className="h-4 w-4" />
                {label("lore.editor.toolbar.bullets", "Bullet list")}
              </DropdownMenu.Item>
              <DropdownMenu.Item
                className="flex cursor-pointer items-center gap-2 rounded-md px-3 py-2 text-sm outline-none transition hover:bg-[#f8f3eb] focus:bg-[#f8f3eb]"
                onSelect={() => activeEditor.chain().focus().toggleOrderedList().run()}
              >
                <ListOrdered className="h-4 w-4" />
                {label("lore.editor.toolbar.numbered", "Numbered list")}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <ToolbarButton
          active={activeEditor.isActive("blockquote")}
          icon={<Quote className="h-4 w-4" />}
          label={label("lore.editor.toolbar.quote", "Quote")}
          onClick={() => activeEditor.chain().focus().toggleBlockquote().run()}
        />
      </div>

      <div className="lore-editor-toolbar__group">
        <ToolbarButton
          active={activeAlignment === "left"}
          icon={<AlignLeft className="h-4 w-4" />}
          label={label("lore.editor.toolbar.alignLeft", "Align left")}
          onClick={() => activeEditor.chain().focus().setTextAlign("left").run()}
        />
        <ToolbarButton
          active={activeAlignment === "center"}
          icon={<AlignCenter className="h-4 w-4" />}
          label={label("lore.editor.toolbar.alignCenter", "Align center")}
          onClick={() => activeEditor.chain().focus().setTextAlign("center").run()}
        />
        <ToolbarButton
          active={activeAlignment === "right"}
          icon={<AlignRight className="h-4 w-4" />}
          label={label("lore.editor.toolbar.alignRight", "Align right")}
          onClick={() => activeEditor.chain().focus().setTextAlign("right").run()}
        />
        <ToolbarButton
          active={activeAlignment === "justify"}
          icon={<AlignJustify className="h-4 w-4" />}
          label={label("lore.editor.toolbar.alignJustify", "Justify")}
          onClick={() => activeEditor.chain().focus().setTextAlign("justify").run()}
        />
      </div>

      <div className="lore-editor-toolbar__group lore-editor-toolbar__group--color">
        <label className="lore-editor-color-control" htmlFor="lore-editor-text-color">
          <Type className="h-4 w-4" />
          <input
            id="lore-editor-text-color"
            onInput={(event) => activeEditor.chain().focus().setColor(event.currentTarget.value).run()}
            title={label("lore.editor.toolbar.textColor", "Text color")}
            type="color"
            value={textStyle.color || "#2f241c"}
          />
        </label>

        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              aria-label={label("lore.editor.toolbar.highlight", "Highlight")}
              className={cn(
                "inline-flex h-9 items-center rounded-md border border-transparent px-2.5 transition",
                highlight.color
                  ? "border-[#e5d7c3] bg-[#f5ede0] text-foreground"
                  : "text-muted-foreground hover:bg-[#f8f3eb] hover:text-foreground",
              )}
              type="button"
            >
              <Highlighter className="h-4 w-4" />
            </button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              className="z-50 rounded-md border border-[#e5d7c3] bg-white p-2 shadow-lg"
              sideOffset={8}
            >
              <div className="flex items-center gap-2">
                {HIGHLIGHT_SWATCHES.map((swatch) => (
                  <button
                    key={swatch}
                    aria-label={label("lore.editor.toolbar.applyHighlight", "Apply highlight")}
                    className={cn(
                      "h-6 w-6 rounded-full border transition-transform hover:scale-105",
                      highlight.color === swatch ? "border-foreground shadow-sm" : "border-border/70",
                    )}
                    onClick={() => activeEditor.chain().focus().toggleHighlight({ color: swatch }).run()}
                    style={{ backgroundColor: swatch }}
                    type="button"
                  />
                ))}
                <Button
                  className="h-8 rounded-md px-2 text-xs"
                  onClick={() => activeEditor.chain().focus().unsetHighlight().run()}
                  type="button"
                  variant="ghost"
                >
                  {label("lore.editor.toolbar.clearHighlight", "Clear")}
                </Button>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>

      <div className="lore-editor-toolbar__group">
        <input
          accept="image/*"
          className="hidden"
          onChange={(event) => {
            void handleImageUpload(event.target.files?.[0] ?? null);
            event.currentTarget.value = "";
          }}
          ref={fileInputRef}
          type="file"
        />
        <ToolbarButton
          icon={<ImagePlus className="h-4 w-4" />}
          label={label("lore.editor.toolbar.uploadImage", "Upload image")}
          onClick={() => fileInputRef.current?.click()}
        />
        <ToolbarButton
          icon={<Link2 className="h-4 w-4" />}
          label={label("lore.editor.toolbar.imageUrl", "Insert image URL")}
          onClick={handleImageUrlInsert}
        />
      </div>
    </div>
  );
}

function SelectionEnhanceBubble({
  config,
  editor,
}: {
  config: NonNullable<LoreEditorProps["enhanceSelection"]>;
  editor: Editor | null;
}) {
  const { t } = useTranslation();
  const [wordCount, setWordCount] = useState(0);
  const previousSelectionRef = useRef<string | null>(null);
  const creditCost = config.getCreditCost(wordCount);
  const disabledReason = config.getDisabledReason(wordCount);
  const disabled = Boolean(disabledReason) || config.loading;

  useEffect(() => {
    if (!editor) {
      return;
    }

    const syncSelection = () => {
      const capture = captureSelectionEnhanceRange(editor);
      setWordCount(capture?.wordCount ?? 0);
      const signature = capture ? `${capture.from}:${capture.to}:${capture.selectedText}` : "empty";
      if (previousSelectionRef.current !== null && previousSelectionRef.current !== signature) {
        config.onSelectionChange?.();
      }
      previousSelectionRef.current = signature;
    };

    editor.on("selectionUpdate", syncSelection);
    syncSelection();

    return () => {
      editor.off("selectionUpdate", syncSelection);
    };
  }, [config, editor]);

  if (!editor) {
    return null;
  }

  return (
    <BubbleMenu
      editor={editor}
      shouldShow={({ editor: activeEditor }) => hasNonEmptySelection(activeEditor)}
      tippyOptions={{ duration: 120, placement: "top" }}
    >
      <div className="flex max-w-[min(32rem,calc(100vw-2rem))] flex-wrap items-center gap-1 rounded-md border border-ink/10 bg-white p-1.5 shadow-lg">
        <ToolbarButton
          active={editor.isActive("bold")}
          icon={<Bold className="h-4 w-4" />}
          label={t("lore.editor.toolbar.bold", { defaultValue: "Bold" })}
          onClick={() => editor.chain().focus().toggleBold().run()}
        />
        <ToolbarButton
          active={editor.isActive("italic")}
          icon={<Italic className="h-4 w-4" />}
          label={t("lore.editor.toolbar.italic", { defaultValue: "Italic" })}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        />
        <div className="mx-1 h-6 w-px bg-ink/10" />
        <Button
          className="h-9 rounded-md px-3 text-xs"
          disabled={disabled}
          onClick={() => {
            const selection = captureSelectionEnhanceRange(editor);
            if (selection) {
              config.onRequest(selection);
            }
          }}
          title={
            disabledReason
              ?? (wordCount > SELECTION_ENHANCE_MAX_WORDS
                ? t("story.ai.enhance.largeSelectionWarning")
                : t("story.ai.enhance.action"))
          }
          type="button"
          variant="secondary"
        >
          {config.loading ? (
            <LoaderCircle className="h-4 w-4 animate-spin" />
          ) : (
            <Sparkles className="h-4 w-4" />
          )}
          <span>{t("story.ai.enhance.action")}</span>
          <span className="rounded bg-white/70 px-1.5 py-0.5 text-[0.65rem] text-muted-foreground">
            {t("story.ai.textEnhancement.creditBadge", {
              cost: creditCost,
            })}
          </span>
        </Button>
        {wordCount > SELECTION_ENHANCE_MAX_WORDS ? (
          <span className="basis-full px-2 pb-1 text-[0.7rem] leading-4 text-amber-700">
            {t("story.ai.enhance.largeSelectionWarning", {
              limit: SELECTION_ENHANCE_MAX_WORDS,
            })}
          </span>
        ) : null}
        {disabledReason ? (
          <span className="basis-full px-2 pb-1 text-[0.7rem] leading-4 text-muted-foreground">
            {disabledReason}
          </span>
        ) : null}
      </div>
    </BubbleMenu>
  );
}

function clampPageIndex(pageIndex: number, pageCount: number) {
  return Math.min(Math.max(pageIndex, 0), Math.max(pageCount - 1, 0));
}

function buildPaginatedTrackStyle(
  pageIndex: number,
): CSSProperties {
  return {
    "--lore-editor-content-height": `${PAGE_CONTENT_HEIGHT_PX}px`,
    "--lore-editor-content-width": `${PAGE_CONTENT_WIDTH_PX}px`,
    "--lore-editor-page-offset": `${pageIndex * PAGE_CONTENT_HEIGHT_PX}px`,
  } as CSSProperties;
}

function getSelectionPageIndex(editor: Editor, pageCount: number) {
  const selectionHead = editor.state.selection.head;

  try {
    const coords = editor.view.coordsAtPos(selectionHead);
    const editorRect = (editor.view.dom as HTMLElement).getBoundingClientRect();
    return clampPageIndex(
      Math.floor((coords.top - editorRect.top) / PAGE_CONTENT_HEIGHT_PX),
      pageCount,
    );
  } catch {
    return 0;
  }
}

function findSelectionPosForPage(editor: Editor, targetPageIndex: number, pageCount: number) {
  const clampedTargetPage = clampPageIndex(targetPageIndex, pageCount);
  const maxPosition = Math.max(editor.state.doc.content.size, 1);
  let low = 1;
  let high = maxPosition;
  let bestPosition = maxPosition;

  while (low <= high) {
    const middle = Math.floor((low + high) / 2);
    const middlePageIndex = getSelectionPageIndexAtPosition(
      editor,
      middle,
      pageCount,
    );

    if (middlePageIndex >= clampedTargetPage) {
      bestPosition = middle;
      high = middle - 1;
    } else {
      low = middle + 1;
    }
  }

  return bestPosition;
}

function getSelectionPageIndexAtPosition(
  editor: Editor,
  position: number,
  pageCount: number,
) {
  try {
    const coords = editor.view.coordsAtPos(position);
    const editorRect = (editor.view.dom as HTMLElement).getBoundingClientRect();
    return clampPageIndex(
      Math.floor((coords.top - editorRect.top) / PAGE_CONTENT_HEIGHT_PX),
      pageCount,
    );
  } catch {
    return 0;
  }
}

function lockPaginatedEditorScroll(
  editor: Editor | null,
  activeEditorShell: HTMLDivElement | null,
) {
  activeEditorShell?.scrollTo({ left: 0, top: 0, behavior: "auto" });

  if (!editor) {
    return;
  }

  const editorElement = editor.view.dom as HTMLElement;
  editorElement.scrollTo({ left: 0, top: 0, behavior: "auto" });
}

function getSearchTerms(value: string) {
  return Array.from(
    new Set(
      value
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, " ")
        .split(/\s+/)
        .filter((term) => term.length > 3),
    ),
  );
}

function selectBestMatchingTextBlock(editor: Editor, text: string) {
  const terms = getSearchTerms(text);

  if (terms.length === 0) {
    return false;
  }

  const matches: Array<{ from: number; score: number; to: number }> = [];

  editor.state.doc.descendants((node, position) => {
    if (!node.isTextblock) {
      return true;
    }

    const nodeText = node.textContent.toLowerCase();
    const score = terms.reduce(
      (count, term) => count + (nodeText.includes(term) ? 1 : 0),
      0,
    );

    if (score > 0) {
      matches.push({
        from: position + 1,
        score,
        to: position + Math.max(1, node.nodeSize - 1),
      });
    }

    return true;
  });

  const bestMatch = matches.sort((first, second) => second.score - first.score)[0];

  if (!bestMatch) {
    return false;
  }

  editor.chain().focus().setTextSelection({
    from: bestMatch.from,
    to: bestMatch.to,
  }).scrollIntoView().run();
  return true;
}

export function LoreEditor({
  autofocus = false,
  contentClassName,
  content,
  enhanceSelection,
  frameClassName,
  onEditorApiChange,
  onChange,
  paginated = true,
  placeholder,
}: LoreEditorProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Array<HTMLElement | null>>([]);
  const measureRef = useRef<HTMLDivElement | null>(null);
  const activeEditorShellRef = useRef<HTMLDivElement | null>(null);
  const activePageRef = useRef(0);
  const [activePageIndex, setActivePageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(1);
  const [renderedMarkup, setRenderedMarkup] = useState("");
  const resolvedContentClassName = useMemo(
    () =>
      cn(
        "lore-editor-document",
        paginated && "lore-editor-document--paginated",
        contentClassName,
      ),
    [contentClassName, paginated],
  );
  const resolvedFrameClassName = useMemo(
    () => cn("lore-editor-viewport", frameClassName),
    [frameClassName],
  );
  const activeTrackStyle = useMemo(
    () => buildPaginatedTrackStyle(activePageIndex),
    [activePageIndex],
  );
  const editor = useEditor({
    autofocus,
    content,
    editorProps: {
      attributes: {
        class: getStoryEditorClassName({
          className: resolvedContentClassName,
          paginated,
        }),
      },
    },
    extensions: buildExtensions(placeholder),
    immediatelyRender: false,
    onUpdate: ({ editor: nextEditor }) => {
      setRenderedMarkup(nextEditor.view.dom.innerHTML);
      onChange(nextEditor.getJSON());
    },
  });

  useEffect(() => {
    if (!editor || !onEditorApiChange) {
      onEditorApiChange?.(null);
      return;
    }

    const api: RichTextEditorApi = {
      replaceDocumentText: (text) => {
        editor
          .chain()
          .focus()
          .setContent(replaceDocumentTextPreservingStructure(editor.getJSON(), text), true)
          .run();
      },
      replaceRangeText: (from, to, text) => {
        editor.chain().focus().setTextSelection({ from, to }).insertContent(text).run();
      },
      selectBestMatchingText: (text) => selectBestMatchingTextBlock(editor, text),
    };

    onEditorApiChange(api);

    return () => {
      onEditorApiChange(null);
    };
  }, [editor, onEditorApiChange]);

  useEffect(() => {
    activePageRef.current = activePageIndex;
  }, [activePageIndex]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    editor.setOptions({
      editorProps: {
        attributes: {
          class: getStoryEditorClassName({
            className: resolvedContentClassName,
            paginated,
          }),
        },
      },
    });
    setRenderedMarkup(editor.view.dom.innerHTML);
  }, [editor, paginated, resolvedContentClassName]);

  useEffect(() => {
    if (!editor) {
      return;
    }

    const currentDocument = editor.getJSON();

    if (JSON.stringify(currentDocument) === JSON.stringify(content)) {
      return;
    }

    if (editor.isFocused) {
      return;
    }

    editor.commands.setContent(content, false);
    requestAnimationFrame(() => {
      setRenderedMarkup(editor.view.dom.innerHTML);
    });
  }, [content, editor]);

  useEffect(() => {
    if (!paginated) {
      return;
    }

    const measureElement = measureRef.current;

    if (!measureElement) {
      return;
    }

    const updatePageCount = () => {
      const contentHeight = measureElement.scrollHeight;
      const nextPageCount = Math.max(
        1,
        Math.ceil(contentHeight / PAGE_CONTENT_HEIGHT_PX),
      );
      setPageCount((currentPageCount) =>
        currentPageCount === nextPageCount ? currentPageCount : nextPageCount,
      );
    };

    updatePageCount();

    const observer = new ResizeObserver(() => {
      updatePageCount();
    });

    observer.observe(measureElement);

    return () => {
      observer.disconnect();
    };
  }, [paginated, renderedMarkup]);

  useEffect(() => {
    setActivePageIndex((currentPageIndex) =>
      clampPageIndex(currentPageIndex, pageCount),
    );
  }, [pageCount]);

  useEffect(() => {
    if (!editor || !paginated) {
      return;
    }

    const syncSelectionPage = () => {
      lockPaginatedEditorScroll(editor, activeEditorShellRef.current);
      const nextPageIndex = getSelectionPageIndex(editor, pageCount);

      if (nextPageIndex === activePageRef.current) {
        return;
      }

      activePageRef.current = nextPageIndex;
      setActivePageIndex(nextPageIndex);
      requestAnimationFrame(() => {
        lockPaginatedEditorScroll(editor, activeEditorShellRef.current);
      });
    };

    editor.on("selectionUpdate", syncSelectionPage);
    editor.on("transaction", syncSelectionPage);
    requestAnimationFrame(syncSelectionPage);

    return () => {
      editor.off("selectionUpdate", syncSelectionPage);
      editor.off("transaction", syncSelectionPage);
    };
  }, [editor, pageCount, paginated]);

  useEffect(() => {
    if (!paginated) {
      return;
    }

    lockPaginatedEditorScroll(editor, activeEditorShellRef.current);
  }, [activePageIndex, editor, paginated]);

  function handleActivatePage(pageIndex: number, clientX: number, clientY: number) {
    if (!editor || pageIndex === activePageRef.current) {
      return;
    }

    activePageRef.current = pageIndex;
    setActivePageIndex(pageIndex);

    requestAnimationFrame(() => {
      if (!editor) return;
      try {
        const coords = editor.view.posAtCoords({ left: clientX, top: clientY });
        if (coords) {
          editor.chain().focus().setTextSelection(coords.pos).run();
        } else {
          const selectionPosition = findSelectionPosForPage(editor, pageIndex, pageCount);
          editor.chain().focus().setTextSelection(selectionPosition).run();
        }
      } catch {
        const selectionPosition = findSelectionPosForPage(editor, pageIndex, pageCount);
        editor.chain().focus().setTextSelection(selectionPosition).run();
      }
    });
  }

  if (!paginated) {
    return (
      <div className="lore-editor-shell">
        <EditorToolbar editor={editor} />
        {enhanceSelection ? (
          <SelectionEnhanceBubble config={enhanceSelection} editor={editor} />
        ) : null}
        <div
          className={getStoryViewportClassName({
            className: resolvedFrameClassName,
            paginated: false,
          })}
          ref={viewportRef}
        >
          <EditorContent editor={editor} />
        </div>
      </div>
    );
  }

  return (
    <div className="lore-editor-shell">
      <EditorToolbar editor={editor} />
      {enhanceSelection ? (
        <SelectionEnhanceBubble config={enhanceSelection} editor={editor} />
      ) : null}
      <div
        className={getStoryViewportClassName({
          className: resolvedFrameClassName,
          paginated: true,
        })}
        ref={viewportRef}
      >
        <div className="lore-editor-page-stack">
          <div
            className="lore-editor-active-layer"
            style={{
              transform: `translateY(${(activePageIndex * (PAGE_SURFACE_HEIGHT_PX + PAGE_GAP_PX)) + PAGE_CONTENT_PADDING_Y_PX}px)`,
            }}
          >
            <div
              className="lore-editor-page-editor"
              ref={activeEditorShellRef}
              style={activeTrackStyle}
            >
              <EditorContent editor={editor} />
            </div>
          </div>

          {Array.from({ length: pageCount }, (_, index) => {
            const isActivePage = index === activePageIndex;
            const pageTrackStyle = buildPaginatedTrackStyle(index);

            return (
              <article
                className="lore-editor-page-shell"
                key={`page-${index + 1}`}
                ref={(element) => {
                  pageRefs.current[index] = element;
                }}
              >
                <div className="lore-editor-page">
                  <div
                    className="lore-editor-page-content"
                    onMouseDown={(event) => {
                      if (!isActivePage) {
                        event.preventDefault();
                        handleActivatePage(index, event.clientX, event.clientY);
                      }
                    }}
                  >
                    {renderedMarkup ? (
                      <div
                        aria-hidden="true"
                        className={cn(
                          "lore-editor-document lore-editor-document--paginated lore-editor-page-replica",
                          isActivePage && "opacity-0",
                        )}
                        dangerouslySetInnerHTML={{ __html: renderedMarkup }}
                        style={pageTrackStyle}
                      />
                    ) : null}
                  </div>

                  <div className="lore-editor-page-number">
                    {index + 1}
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>

      <div aria-hidden="true" className="lore-editor-measure-shell">
        <div
          className="lore-editor-document lore-editor-document--measure"
          dangerouslySetInnerHTML={{
            __html:
              renderedMarkup
              || "<p></p>",
          }}
          ref={measureRef}
        />
      </div>
    </div>
  );
}
